import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

type Props = { children: ReactNode; resetKey: string };
type State = { error: Error | null };

/** Keeps the shell alive when one page crashes or its chunk fails to load. */
class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Page crashed:", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const chunk = /dynamically imported module|Loading chunk|Failed to fetch/i.test(this.state.error.message);
    return (
      <section role="alert" className="mx-auto mt-16 max-w-lg rounded-2xl border bg-card p-8 text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[hsl(var(--rose-50))] text-[hsl(var(--rose))] flex items-center justify-center">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <h1 className="font-serif text-2xl font-semibold">
          {chunk ? "This page didn't load" : "Something broke on this page"}
        </h1>
        <p className="mt-2 text-sm text-ink-2">
          {chunk
            ? "The admin may have been updated. Reload to get the latest version."
            : "The rest of the admin still works. Try again, or pick another page."}
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button onClick={() => (chunk ? window.location.reload() : this.setState({ error: null }))} className="rounded-xl">
            {chunk ? "Reload" : "Try again"}
          </Button>
        </div>
      </section>
    );
  }
}

export default RouteErrorBoundary;
